/**
 * Cheap, dependency-free runtime validation of the loaded data.
 *
 * For full JSON-schema validation (used in CI / build step), prefer ajv against
 * /data/schema/*.json. This module exists so the app can fail fast in the
 * browser when a dataset is malformed (e.g. after a future content edit).
 */
import type { Counter, Move, Position } from '../types/domain';

export type ValidationResult = {
  ok: boolean;
  errors: string[];
  warnings: string[];
};

export function validate(
  positions: Position[],
  moves: Move[],
  counters: Counter[],
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  const posIds = new Set(positions.map(p => p.id));
  const moveIds = new Set(moves.map(m => m.id));
  const counterIds = new Set(counters.map(c => c.id));

  // Uniqueness
  if (posIds.size !== positions.length) errors.push('duplicate position id');
  if (moveIds.size !== moves.length) errors.push('duplicate move id');
  if (counterIds.size !== counters.length) errors.push('duplicate counter id');

  // Move references
  for (const m of moves) {
    if (!posIds.has(m.from)) errors.push(`Move ${m.id}: from "${m.from}" not found`);
    if (!posIds.has(m.to)) errors.push(`Move ${m.id}: to "${m.to}" not found`);
    // A self-loop is normally a data error, but some techniques really do
    // return you to where you started — breaking a grip, a wrist snap —
    // and the graph should say that rather than invent a destination.
    // Only an explicit flag excuses it, so accidental loops still fail.
    if (m.from === m.to && !m.noPositionalChange)
      errors.push(`Move ${m.id}: self-loop`);
    for (const cid of m.counters ?? []) {
      if (!counterIds.has(cid)) errors.push(`Move ${m.id}: counter "${cid}" not found`);
    }
  }

  // Counter references — counters are structurally moves, so the same
  // from/to/category invariants apply on top of the counter-specific
  // intercepts/result fields.
  for (const c of counters) {
    if (!moveIds.has(c.intercepts))
      errors.push(`Counter ${c.id}: intercepts "${c.intercepts}" not found`);
    if (!posIds.has(c.result))
      errors.push(`Counter ${c.id}: result "${c.result}" not found`);
    if (!c.from || !posIds.has(c.from))
      errors.push(`Counter ${c.id}: from "${c.from}" not found`);
    if (!c.to || !posIds.has(c.to))
      errors.push(`Counter ${c.id}: to "${c.to}" not found`);
    if (c.to && c.result && c.to !== c.result)
      warnings.push(`Counter ${c.id}: to "${c.to}" differs from result "${c.result}"`);
  }

  // Soft checks (warnings)
  for (const p of positions) {
    if (!p.description || p.description.length < 12)
      warnings.push(`Position ${p.id}: very short description`);
  }

  return { ok: errors.length === 0, errors, warnings };
}
